import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase,
  Search,
  PlusCircle,
  RefreshCcw,
  Edit3,
  Trash2,
  PieChart,
  Activity,
  Users,
  Globe,
  Download,
  X,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  TrendingUp,
  TrendingDown,
  ArrowLeft,
  Layers,
  DollarSign,
  Target,
  Zap,
  ChevronRight,
  Bot,
  ArrowUpRight,
  ArrowDownRight,
  ShoppingCart
} from 'lucide-react';
import { useRole } from '../hooks/useRole';
import { cn } from '../lib/utils';
import {
  calculatePortfolioMetrics,
  calculatePositionWeight,
  isActivePosition,
} from '../utils/portfolioMetrics';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string; subText?: string }> =
  ({ label, value, icon, color, subText }) => (
    <motion.div
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className="text-2xl font-bold text-white tracking-tight">{value}</div>
          {subText && <div className="text-[10px] text-slate-500 mt-2 font-medium">{subText}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

// ============== Interfaces ==============
interface AssetManager {
  id: number;
  assetManagerName: string;
}

interface PortfolioManager {
  id: number;
  portfolioManagerName: string;
}

interface Portfolio {
  id: number;
  portfolioName: string;
  portfolioCode: string;
  status: string;
  tradingEnabled: boolean;
  assetManager?: AssetManager;
  portfolioManager?: PortfolioManager;
  description?: string;
  // F-PUB-026 / F-PUB-038: mandate settings chosen at portfolio setup.
  taxLotStrategy?: string;
  targetDuration?: number;
}

interface TradeSuggestion {
  securityCusip: string;
  securityName: string;
  transactionType: 'BUY' | 'SELL';
  amount: number;
  rationale: string;
  expectedImpact: string;
}

interface Position {
  id: number;
  securityName: string;
  cusip: string;
  quantity: number;
  marketValue: number;
  unrealizedPnl: number;
  assetClassName?: string;
  assetSubClassName?: string;
}

type TabType = 'overview' | 'positions' | 'ai-suggestions';

const PortfolioManagementScreen: React.FC = () => {
  const { canEdit } = useRole();
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [assetManagers, setAssetManagers] = useState<AssetManager[]>([]);
  const [portfolioManagers, setPortfolioManagers] = useState<PortfolioManager[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Portfolio detail view state
  const [selectedPortfolio, setSelectedPortfolio] = useState<Portfolio | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [positions, setPositions] = useState<Position[]>([]);
  const [isLoadingPositions, setIsLoadingPositions] = useState(false);
  const positionsRequestId = useRef(0);

  // AI Suggestions state
  const [suggestions, setSuggestions] = useState<TradeSuggestion[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<Portfolio>>({
    portfolioName: '',
    portfolioCode: '',
    status: 'ACTIVE',
    tradingEnabled: true,
    description: ''
  });

  const fetchPortfolios = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/dimensions/portfolios', {
        params: { includeInactive: true },
      });
      const data = response.data?.content || response.data || [];
      setPortfolios(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching portfolios:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchMetadata = useCallback(async () => {
    try {
      const [amRes, pmRes] = await Promise.all([
        axios.get('/api/dimensions/asset-managers'),
        axios.get('/api/dimensions/portfolio-managers')
      ]);
      setAssetManagers(amRes.data?.content || amRes.data || []);
      setPortfolioManagers(pmRes.data?.content || pmRes.data || []);
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  }, []);

  const fetchPositions = useCallback(async (portfolioId: number) => {
    const requestId = ++positionsRequestId.current;
    setIsLoadingPositions(true);
    try {
      const pageSize = 500;
      const allPositions: Position[] = [];
      let page = 0;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await axios.get(`/api/positions/portfolio/${portfolioId}`, {
          params: { page, size: pageSize },
        });
        const responseBody = response.data;

        if (Array.isArray(responseBody)) {
          allPositions.push(...responseBody);
          break;
        }

        const pagePositions = Array.isArray(responseBody?.content)
          ? responseBody.content
          : [];
        allPositions.push(...pagePositions);

        const totalPages = Number(responseBody?.totalPages ?? 0);
        hasMorePages = totalPages > 0
          ? page + 1 < totalPages
          : pagePositions.length === pageSize;
        page += 1;
      }

      if (requestId === positionsRequestId.current) {
        setPositions(allPositions);
      }
    } catch (error) {
      console.error('Error fetching positions:', error);
      if (requestId === positionsRequestId.current) {
        setPositions([]);
      }
    } finally {
      if (requestId === positionsRequestId.current) {
        setIsLoadingPositions(false);
      }
    }
  }, []);

  const fetchAISuggestions = useCallback(async (portfolioId: number) => {
    setIsLoadingSuggestions(true);
    setSuggestionsError(null);
    try {
      const response = await axios.post('/api/ai/trade-suggestions', {
        portfolioId,
        assetClass: 'Fixed Income'
      });

      if (response.data?.success && response.data?.suggestions) {
        setSuggestions(response.data.suggestions);
      } else if (response.data?.suggestions) {
        setSuggestions(response.data.suggestions);
      } else {
        setSuggestions([]);
      }
    } catch (error: any) {
      console.error('Error fetching AI suggestions:', error);
      setSuggestionsError(error.response?.data?.message || 'Failed to generate AI suggestions. Please try again.');
      setSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, []);

  useEffect(() => {
    fetchPortfolios();
    fetchMetadata();
  }, [fetchPortfolios, fetchMetadata]);

  useEffect(() => {
    if (selectedPortfolio) {
      fetchPositions(selectedPortfolio.id);
    }
  }, [selectedPortfolio, fetchPositions]);

  useEffect(() => {
    if (selectedPortfolio && activeTab === 'ai-suggestions') {
      fetchAISuggestions(selectedPortfolio.id);
    }
  }, [selectedPortfolio, activeTab, fetchAISuggestions]);

  const handleSelectPortfolio = (portfolio: Portfolio) => {
    setPositions([]);
    setSuggestions([]);
    setSelectedPortfolio(portfolio);
    setActiveTab('overview');
  };

  const handleBackToList = () => {
    positionsRequestId.current += 1;
    setSelectedPortfolio(null);
    setActiveTab('overview');
    setSuggestions([]);
    setPositions([]);
  };

  const handleCreateTradeFromSuggestion = (suggestion: TradeSuggestion) => {
    // Navigate to trade entry with pre-filled data
    const tradeData = {
      portfolioId: selectedPortfolio?.id,
      cusip: suggestion.securityCusip,
      securityName: suggestion.securityName,
      transactionType: suggestion.transactionType,
      amount: suggestion.amount,
      rationale: suggestion.rationale
    };
    // Store in session storage for trade entry screen to pick up
    sessionStorage.setItem('prefillTrade', JSON.stringify(tradeData));
    // Navigate to trade entry (assuming react-router or similar)
    window.location.href = '/trade-entry/fixed-income';
  };

  const handleAdd = () => {
    setFormData({
      portfolioName: '',
      portfolioCode: '',
      status: 'ACTIVE',
      tradingEnabled: true,
      description: '',
      // F-PUB-026: an explicit default the user can change, not a silent DB default.
      taxLotStrategy: 'FIFO'
    });
    setIsEditing(false);
    setShowModal(true);
    setError(null);
  };

  const handleEdit = (portfolio: Portfolio) => {
    setFormData({
      ...portfolio,
      assetManager: portfolio.assetManager,
      portfolioManager: portfolio.portfolioManager
    });
    setIsEditing(true);
    setShowModal(true);
    setError(null);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this portfolio?')) return;

    try {
      await axios.delete(`/api/dimensions/portfolios/${id}`);
      setSuccessMessage('Portfolio deleted successfully');
      fetchPortfolios();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete portfolio');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const payload: any = {
        ...formData,
        assetManager: formData.assetManager?.id ? { id: formData.assetManager.id } : null,
        portfolioManager: formData.portfolioManager?.id ? { id: formData.portfolioManager.id } : null
      };

      if (isEditing && formData.id) {
        await axios.put(`/api/dimensions/portfolios/${formData.id}`, payload);
        setSuccessMessage('Portfolio updated successfully');
      } else {
        await axios.post('/api/dimensions/portfolios', payload);
        setSuccessMessage('Portfolio created successfully');
      }
      setShowModal(false);
      fetchPortfolios();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save portfolio');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredPortfolios = useMemo(() => {
    if (!searchTerm) return portfolios;
    const s = searchTerm.toLowerCase();
    return portfolios.filter(p =>
      p.portfolioName?.toLowerCase().includes(s) ||
      p.portfolioCode?.toLowerCase().includes(s)
    );
  }, [portfolios, searchTerm]);

  const portfolioMetrics = useMemo(
    () => calculatePortfolioMetrics(positions),
    [positions],
  );
  const activePositions = useMemo(
    () => positions.filter(isActivePosition),
    [positions],
  );

  const formatCurrency = (value: number) => {
    if (Math.abs(value) >= 1000000000) {
      return `$${(value / 1000000000).toFixed(2)}B`;
    }
    if (Math.abs(value) >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    }
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const ActionCellRenderer = (params: any) => (
    <div className="flex items-center gap-2 h-full">
      <button
        onClick={(e) => { e.stopPropagation(); handleEdit(params.data); }}
        className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-all border border-blue-500/20"
        title="Edit"
      >
        <Edit3 className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); handleDelete(params.data.id); }}
        className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all border border-red-500/20"
        title="Delete"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );

  const StatusCellRenderer = (p: any) => {
    const active = p.value === 'ACTIVE';
    return (
      <span className={cn(
        "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider",
        active ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-800 text-slate-400"
      )}>
        {p.value}
      </span>
    );
  };

  const columnDefs = useMemo<ColDef[]>(() => [
    { field: 'id', headerName: 'ID', width: 80, pinned: 'left', cellStyle: { fontWeight: 'bold', color: '#60a5fa' } as any },
    { field: 'portfolioName', headerName: 'Portfolio Name', flex: 2, cellStyle: { fontWeight: '600' } as any },
    { field: 'portfolioCode', headerName: 'Code', width: 150, cellStyle: { fontFamily: 'monospace', color: '#94a3b8' } as any },
    {
      field: 'assetManager',
      headerName: 'Asset Manager',
      flex: 1,
      valueGetter: (p) => p.data?.assetManager?.assetManagerName || ''
    },
    {
      field: 'portfolioManager',
      headerName: 'Portfolio Manager',
      flex: 1,
      valueGetter: (p) => p.data?.portfolioManager?.portfolioManagerName || ''
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      cellRenderer: StatusCellRenderer
    },
    {
      field: 'tradingEnabled',
      headerName: 'Trading',
      width: 105,
      valueFormatter: (p) => p.value === false ? 'Disabled' : 'Enabled'
    },
    {
      headerName: 'Actions',
      width: 120,
      cellRenderer: ActionCellRenderer,
      sortable: false,
      filter: false
    }
  ], []);

  // Portfolio Detail View
  if (selectedPortfolio) {
    return (
      <Container>
        <div className="max-w-[1800px] mx-auto space-y-6">
          {/* Header with Back Button */}
          <div className="flex items-center gap-4">
            <button
              onClick={handleBackToList}
              className="p-2 rounded-xl bg-slate-900 border border-white/10 text-slate-400 hover:text-white transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <Briefcase className="w-8 h-8 text-blue-500" />
                {selectedPortfolio.portfolioName}
              </h1>
              <p className="text-slate-500 font-medium mt-1">
                {selectedPortfolio.portfolioCode} • {selectedPortfolio.assetManager?.assetManagerName || 'No Asset Manager'}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => fetchAISuggestions(selectedPortfolio.id)}
                disabled={isLoadingSuggestions}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 rounded-xl text-sm font-bold text-white shadow-lg shadow-violet-600/20 hover:bg-violet-500 transition-all disabled:opacity-50"
              >
                <Sparkles className={cn("w-4 h-4", isLoadingSuggestions && "animate-pulse")} />
                Refresh AI Insights
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <motion.div variants={staggerContainer} initial="initial" animate="animate" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              label="Net Asset Value"
              value={isLoadingPositions ? '—' : formatCurrency(portfolioMetrics.netAssetValue)}
              icon={<DollarSign className="w-5 h-5" />}
              color="#3b82f6"
              subText="Net IBOR market value"
            />
            <StatCard
              label="Assets Under Management"
              value={isLoadingPositions ? '—' : formatCurrency(portfolioMetrics.assetsUnderManagement)}
              icon={<Layers className="w-5 h-5" />}
              color="#8b5cf6"
              subText="Gross IBOR market value"
            />
            <StatCard
              label="Positions"
              value={isLoadingPositions ? '—' : String(portfolioMetrics.positionCount)}
              icon={<PieChart className="w-5 h-5" />}
              color="#10b981"
              subText="Active holdings"
            />
            <StatCard
              label="AI Suggestions"
              value={String(suggestions.length)}
              icon={<Sparkles className="w-5 h-5" />}
              color="#f59e0b"
              subText="Actionable insights"
            />
          </motion.div>

          {/* Tab Navigation */}
          <GlassCard className="p-1">
            <div className="flex gap-1">
              {[
                { id: 'overview' as TabType, label: 'Overview', icon: <Activity className="w-4 h-4" /> },
                { id: 'positions' as TabType, label: 'Positions', icon: <Layers className="w-4 h-4" /> },
                { id: 'ai-suggestions' as TabType, label: 'AI Suggestions', icon: <Sparkles className="w-4 h-4" /> }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all",
                    activeTab === tab.id
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                      : "text-slate-400 hover:text-white hover:bg-slate-800"
                  )}
                >
                  {tab.icon}
                  {tab.label}
                  {tab.id === 'ai-suggestions' && suggestions.length > 0 && (
                    <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-400">
                      {suggestions.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </GlassCard>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="grid grid-cols-1 xl:grid-cols-2 gap-6"
              >
                <GlassCard className="p-6">
                  <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-400" />
                    Portfolio Details
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between py-3 border-b border-white/5">
                      <span className="text-slate-400">Portfolio Code</span>
                      <span className="text-white font-mono">{selectedPortfolio.portfolioCode}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-white/5">
                      <span className="text-slate-400">Status</span>
                      <span className={cn(
                        "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase",
                        selectedPortfolio.status === 'ACTIVE' ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-800 text-slate-400"
                      )}>
                        {selectedPortfolio.status}
                      </span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-white/5">
                      <span className="text-slate-400">Asset Manager</span>
                      <span className="text-white">{selectedPortfolio.assetManager?.assetManagerName || '-'}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-white/5">
                      <span className="text-slate-400">Portfolio Manager</span>
                      <span className="text-white">{selectedPortfolio.portfolioManager?.portfolioManagerName || '-'}</span>
                    </div>
                    {selectedPortfolio.description && (
                      <div className="py-3">
                        <span className="text-slate-400 block mb-2">Description</span>
                        <span className="text-white text-sm">{selectedPortfolio.description}</span>
                      </div>
                    )}
                  </div>
                </GlassCard>

                <GlassCard className="p-6">
                  <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-blue-400" />
                    Asset Allocation
                  </h3>
                  {isLoadingPositions ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCcw className="w-5 h-5 animate-spin text-blue-400 mr-3" />
                      <span className="text-slate-400">Loading IBOR allocation...</span>
                    </div>
                  ) : portfolioMetrics.allocations.length === 0 ? (
                    <div className="py-12 text-center text-slate-500 italic">
                      No IBOR positions available for allocation
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {portfolioMetrics.allocations.map((item, index) => (
                      <div key={item.label} className="space-y-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-400 font-medium">{item.label}</span>
                          <span className="text-white font-bold">{item.percentage.toFixed(2)}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-1000"
                            style={{
                              width: `${item.percentage}%`,
                              backgroundColor: ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#64748b'][index % 5],
                            }}
                          />
                        </div>
                      </div>
                      ))}
                    </div>
                  )}
                </GlassCard>
              </motion.div>
            )}

            {activeTab === 'positions' && (
              <motion.div
                key="positions"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <GlassCard className="overflow-hidden">
                  <div className="p-4 border-b border-white/5 bg-slate-900/30">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <Layers className="w-5 h-5 text-blue-400" />
                      Current Holdings
                    </h3>
                  </div>
                  {isLoadingPositions ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCcw className="w-6 h-6 animate-spin text-blue-400 mr-3" />
                      <span className="text-slate-400">Loading positions...</span>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5 bg-slate-950/30">
                            <th className="py-3 px-4">Security</th>
                            <th className="py-3 px-4">CUSIP</th>
                            <th className="py-3 px-4 text-right">Quantity</th>
                            <th className="py-3 px-4 text-right">Market Value</th>
                            <th className="py-3 px-4 text-right">Weight</th>
                            <th className="py-3 px-4 text-right">Unrealized P&L</th>
                          </tr>
                        </thead>
                        <tbody className="text-sm">
                          {activePositions.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="py-12 text-center text-slate-500 italic">
                                No positions found for this portfolio
                              </td>
                            </tr>
                          ) : (
                            activePositions.map((pos, i) => (
                              <tr key={pos.id || i} className="hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                                <td className="py-3 px-4 font-semibold text-slate-200">{pos.securityName}</td>
                                <td className="py-3 px-4 font-mono text-slate-400">{pos.cusip}</td>
                                <td className="py-3 px-4 text-right font-mono text-white">{pos.quantity?.toLocaleString()}</td>
                                <td className="py-3 px-4 text-right font-bold text-white">{formatCurrency(pos.marketValue || 0)}</td>
                                <td className="py-3 px-4 text-right text-slate-400">
                                  {calculatePositionWeight(pos, portfolioMetrics.assetsUnderManagement).toFixed(2)}%
                                </td>
                                <td className={cn(
                                  "py-3 px-4 text-right font-bold",
                                  (pos.unrealizedPnl || 0) >= 0 ? "text-emerald-400" : "text-red-400"
                                )}>
                                  {(pos.unrealizedPnl || 0) >= 0 ? '+' : ''}{formatCurrency(pos.unrealizedPnl || 0)}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </GlassCard>
              </motion.div>
            )}

            {activeTab === 'ai-suggestions' && (
              <motion.div
                key="ai-suggestions"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                {/* AI Header */}
                <GlassCard className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                      <Bot className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white">AI Trade Advisor</h3>
                      <p className="text-slate-400 text-sm">
                        Intelligent recommendations based on portfolio analysis, market conditions, and investment objectives.
                      </p>
                    </div>
                    <button
                      onClick={() => fetchAISuggestions(selectedPortfolio.id)}
                      disabled={isLoadingSuggestions}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-50"
                    >
                      <RefreshCcw className={cn("w-4 h-4", isLoadingSuggestions && "animate-spin")} />
                      Regenerate
                    </button>
                  </div>
                </GlassCard>

                {suggestionsError && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-center gap-3 text-amber-400">
                    <AlertCircle className="w-5 h-5" />
                    <span className="text-sm">{suggestionsError}</span>
                  </div>
                )}

                {/* Suggestions List */}
                {isLoadingSuggestions ? (
                  <GlassCard className="p-12">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500/20 to-purple-600/20 flex items-center justify-center mb-4">
                        <Sparkles className="w-8 h-8 text-violet-400 animate-pulse" />
                      </div>
                      <p className="text-white font-bold mb-2">Analyzing Portfolio...</p>
                      <p className="text-slate-500 text-sm">AI is generating personalized trade recommendations</p>
                    </div>
                  </GlassCard>
                ) : suggestions.length === 0 ? (
                  <GlassCard className="p-12">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center mb-4">
                        {suggestionsError ? (
                          <AlertCircle className="w-8 h-8 text-amber-400" />
                        ) : (
                          <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                        )}
                      </div>
                      <p className="text-white font-bold mb-2">
                        {suggestionsError ? 'AI Advisor Unavailable' : 'No Suggestions Available'}
                      </p>
                      <p className="text-slate-500 text-sm">
                        {suggestionsError
                          ? 'Suggestions could not be generated for this portfolio right now. Try again later or review the portfolio manually.'
                          : 'No trade recommendations are currently available for this portfolio.'}
                      </p>
                    </div>
                  </GlassCard>
                ) : (
                  <div className="space-y-4">
                    {suggestions.map((suggestion, index) => (
                      <GlassCard key={index} className="p-6 hover:border-blue-500/30 transition-all">
                        <div className="flex items-start gap-6">
                          {/* Trade Type Badge */}
                          <div className={cn(
                            "w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0",
                            suggestion.transactionType === 'BUY'
                              ? "bg-emerald-500/10 border border-emerald-500/20"
                              : "bg-red-500/10 border border-red-500/20"
                          )}>
                            {suggestion.transactionType === 'BUY' ? (
                              <ArrowUpRight className="w-7 h-7 text-emerald-400" />
                            ) : (
                              <ArrowDownRight className="w-7 h-7 text-red-400" />
                            )}
                          </div>

                          {/* Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                              <span className={cn(
                                "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider",
                                suggestion.transactionType === 'BUY'
                                  ? "bg-emerald-500/10 text-emerald-400"
                                  : "bg-red-500/10 text-red-400"
                              )}>
                                {suggestion.transactionType}
                              </span>
                              <span className="text-white font-bold">{suggestion.securityName}</span>
                              <span className="text-slate-500 font-mono text-sm">{suggestion.securityCusip}</span>
                            </div>

                            <div className="flex items-center gap-4 mb-3">
                              <div>
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Amount</span>
                                <div className="text-xl font-bold text-white">{formatCurrency(suggestion.amount)}</div>
                              </div>
                              <div className="h-8 w-px bg-white/10" />
                              <div>
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Expected Impact</span>
                                <div className="text-sm font-bold text-blue-400">{suggestion.expectedImpact}</div>
                              </div>
                            </div>

                            <p className="text-slate-400 text-sm leading-relaxed">
                              {suggestion.rationale}
                            </p>
                          </div>

                          {/* Action Button */}
                          <button
                            onClick={() => handleCreateTradeFromSuggestion(suggestion)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-xl text-sm font-bold text-white shadow-lg shadow-blue-600/20 transition-all flex-shrink-0"
                          >
                            <ShoppingCart className="w-4 h-4" />
                            Create Trade
                          </button>
                        </div>
                      </GlassCard>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Container>
    );
  }

  // Portfolio List View
  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <Briefcase className="w-8 h-8 text-blue-500" />
                Portfolio Management
              </h1>
              <p className="text-slate-500 font-medium mt-1">Select a portfolio to view details and AI-powered trade suggestions</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => fetchPortfolios()}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
              >
                <RefreshCcw className={cn("w-4 h-4", isLoading && "animate-spin")} />
                Refresh
              </button>
              {canEdit && (
                <button
                  onClick={handleAdd}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-xl text-sm font-bold text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500 transition-all"
                >
                  <PlusCircle className="w-4 h-4" />
                  Add Portfolio
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard label="Total Portfolios" value={String(portfolios.length)} icon={<Briefcase className="w-5 h-5" />} color="#3b82f6" />
            <StatCard label="Asset Managers" value={String(assetManagers.length)} icon={<Globe className="w-5 h-5" />} color="#8b5cf6" />
            <StatCard label="Account Managers" value={String(portfolioManagers.length)} icon={<Users className="w-5 h-5" />} color="#10b981" />
            <StatCard label="Active Status" value="100%" icon={<Activity className="w-5 h-5" />} color="#f59e0b" />
          </div>
        </motion.div>

        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3 text-emerald-400 font-bold"
          >
            <CheckCircle2 className="w-5 h-5" />
            {successMessage}
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400 font-bold"
          >
            <AlertCircle className="w-5 h-5" />
            {error}
          </motion.div>
        )}

        <GlassCard className="flex flex-col h-[calc(100vh-400px)] min-h-[500px]">
          <div className="p-4 border-b border-white/5 flex items-center justify-between bg-slate-900/30">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                <input
                  type="text"
                  placeholder="Filter portfolios..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-slate-950/50 border border-white/10 rounded-xl text-sm text-white placeholder:text-slate-700 focus:outline-none focus:border-blue-500/50 transition-all w-80"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <Sparkles className="w-4 h-4 text-violet-400" />
              Click a portfolio to view AI suggestions
            </div>
          </div>

          <div className="flex-1 relative ag-theme-alpine-dark">
            <AgGridReact
              rowData={filteredPortfolios}
              columnDefs={columnDefs}
              animateRows={true}
              pagination={true}
              paginationPageSize={25}
              headerHeight={48}
              rowHeight={44}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              onGridReady={(p) => p.api.sizeColumnsToFit()}
              onRowClicked={(e) => handleSelectPortfolio(e.data)}
              getRowStyle={(params: any) => ({
                backgroundColor: params.node.rowIndex % 2 === 0 ? 'rgba(15, 23, 42, 0.3)' : 'transparent',
                borderBottom: '1px solid rgba(255, 255, 255, 0.03)',
                cursor: 'pointer'
              })}
            />
          </div>
        </GlassCard>
      </div>

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-950/80 backdrop-blur-md"
              onClick={() => setShowModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-2xl bg-slate-900 border border-white/10 rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="p-8 border-b border-white/5 bg-slate-950/30 flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-white tracking-tight">
                    {isEditing ? 'Edit Portfolio' : 'Add New Portfolio'}
                  </h2>
                  <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-1">Institutional Account Definition</p>
                </div>
                <button onClick={() => setShowModal(false)} className="p-2 text-slate-500 hover:text-white transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-8 space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Portfolio Name</label>
                    <input
                      value={formData.portfolioName || ''}
                      onChange={e => setFormData({ ...formData, portfolioName: e.target.value })}
                      className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all"
                      placeholder="e.g. Aggressive Growth Fund"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Portfolio Code</label>
                    <input
                      value={formData.portfolioCode || ''}
                      onChange={e => setFormData({ ...formData, portfolioCode: e.target.value })}
                      className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all font-mono"
                      placeholder="e.g. AGG-001"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Asset Manager</label>
                    <select
                      value={formData.assetManager?.id || ''}
                      onChange={e => {
                        const am = assetManagers.find(a => a.id === parseInt(e.target.value));
                        setFormData({ ...formData, assetManager: am });
                      }}
                      className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none"
                    >
                      <option value="">Select Asset Manager</option>
                      {assetManagers.map(am => <option key={am.id} value={am.id}>{am.assetManagerName}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Portfolio Manager</label>
                    <select
                      value={formData.portfolioManager?.id || ''}
                      onChange={e => {
                        const pm = portfolioManagers.find(p => p.id === parseInt(e.target.value));
                        setFormData({ ...formData, portfolioManager: pm });
                      }}
                      className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none"
                    >
                      <option value="">Select Portfolio Manager</option>
                      {portfolioManagers.map(pm => <option key={pm.id} value={pm.id}>{pm.portfolioManagerName}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Status</label>
                    <select
                      value={formData.status || 'ACTIVE'}
                      onChange={e => setFormData({ ...formData, status: e.target.value })}
                      className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none"
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="INACTIVE">INACTIVE</option>
                      <option value="ARCHIVED">ARCHIVED</option>
                      <option value="CLOSED">CLOSED</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-950 px-4 py-3 self-end">
                    <input
                      type="checkbox"
                      checked={formData.tradingEnabled !== false}
                      onChange={e => setFormData({ ...formData, tradingEnabled: e.target.checked })}
                      className="h-4 w-4 rounded border-white/20 bg-slate-900 text-blue-500"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-white">Trading enabled</span>
                      <span className="block text-[10px] text-slate-500">Show in OMS and agent selectors</span>
                    </span>
                  </label>
                </div>

                {/* F-PUB-026: the relief engine honours dim_portfolio.tax_lot_strategy,
                    but there was no way to choose it — every portfolio was stuck on
                    the DB default of FIFO regardless of mandate. */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Tax Lot Strategy</label>
                    <select
                      value={formData.taxLotStrategy || 'FIFO'}
                      onChange={e => setFormData({ ...formData, taxLotStrategy: e.target.value })}
                      className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none"
                    >
                      <option value="FIFO">FIFO — First In, First Out</option>
                      <option value="LIFO">LIFO — Last In, First Out</option>
                      <option value="MAXIMUM_GAIN">Maximum Gain</option>
                      <option value="MAXIMUM_LOSS">Maximum Loss</option>
                      <option value="MINIMUM_TAX">Minimum Tax</option>
                      <option value="PRO_RATA">Pro Rata</option>
                      <option value="SPECIFIC_LOT">Specific Lot</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Target Duration (yrs)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.targetDuration ?? ''}
                      onChange={e => setFormData({ ...formData, targetDuration: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                      className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all"
                      placeholder="Leave blank if no target"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Description</label>
                  <textarea
                    value={formData.description || ''}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all min-h-[100px]"
                    placeholder="Mandate details, investment objectives..."
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => setShowModal(false)}
                    className="flex-1 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-2xl transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !formData.portfolioName || !formData.portfolioCode}
                    className="flex-1 py-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold rounded-2xl transition-all shadow-xl shadow-blue-600/20 flex items-center justify-center gap-3"
                  >
                    {isSaving ? (
                      <RefreshCcw className="w-5 h-5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-5 h-5" />
                    )}
                    {isEditing ? 'Update Portfolio' : 'Create Portfolio'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default PortfolioManagementScreen;
